import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FileDown, UserCheck, Settings2 } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { CombinedScenarioResult, DashboardSummary } from "@/lib/calculationEngine";
import { buildCommitmentPageData } from "@/lib/calculationEngine";
import { buildCommitmentPageData as buildCommitmentPageDataUS } from "@/lib/usaLoanEngine";
import type { LoanSetupData } from "@/components/LoanSetup";
import type { PolicyLoanInput, PlannedRepaymentStrategyInput } from "@/lib/calculationEngine";
import {
  buildThirdPartyLoanHtml,
  DEFAULT_THIRD_PARTY_LEGAL,
  type ThirdPartyLegalOptions,
  type CollateralKind,
  type PrepaymentKind,
} from "@/components/ThirdPartyLoanDoc";
import { openPrintWindow, printHtmlInPage } from "@/lib/printWindow";
import { buildCommitmentDocModel } from "@/lib/pdf/commitmentDocModel";
import { toast } from "sonner";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";
import { usaCarrierLabels, type USACarrier } from "@/lib/usaCarrierData";
import type { SolveForPaymentResult } from "@/lib/paymentSolver";
import { e } from "@/lib/htmlSanitize";
import { summariseMarginChange, finalScheduledPayment } from "@/lib/reportFigures";
import {
  FUTURE_ME_QUESTIONS,
  stewardshipCompleted,
  stewardshipScore,
  stewardshipRating,
  futureMeHasContent,
  STEWARDSHIP_ITEMS,
  SECTION_TITLES,
  type StewardshipManual,
  type StewardshipAuto,
  type StewardshipState,
  type FutureMeState,
} from "@/components/StewardshipAndFutureMe";
import { buildExportFilename, reserveNextLoanNumber, resolveClientInitials, formatLoanId } from "@/lib/loanId";
import { formatPurpose, findCategory, type PurposeSelection } from "@/lib/purpose";
import type { LumpSumConfig } from "@/components/LumpSumScenarios";

interface PrintScheduleProps {
  loanSetup: LoanSetupData;
  combined: CombinedScenarioResult;
  policyLoanInput: Omit<PolicyLoanInput, "payments" | "projectionEndDate">;
  repaymentStrategy: PlannedRepaymentStrategyInput;
  jurisdiction: Jurisdiction;
  solvedPayment?: SolveForPaymentResult | null;
  dashboard: DashboardSummary;
  loanId: string;
  stewardship: StewardshipState;
  futureMe: FutureMeState;
  policyOwnerName: string;
  onPolicyOwnerNameChange: (v: string) => void;
  loanBorrowerName: string;
  onLoanBorrowerNameChange: (v: string) => void;
  purpose?: PurposeSelection | null;
  lumpSumConfig?: LumpSumConfig | null;
  lumpSumCombined?: CombinedScenarioResult | null;
  lumpSumDashboard?: DashboardSummary | null;
  /** Optional callback fired when the user generates a Commitment Agreement PDF.
   *  Used by the dashboard to promote a scenario from draft/saved → committed. */
  onCommitmentGenerated?: () => void;
  /** Saved scenario being edited; enables the optional payment-tracking ledger. */
  scenarioId?: string | null;
}

interface ReportOptions {
  includeCommitmentSchedule: boolean;
  includeAmortization: boolean;
  includeAdvisor: boolean;
  includeCharts: boolean;
  includeNotes: boolean;
  includePayoffSummary: boolean;
  includeFinancingMargin: boolean;
  includeInterestVolume: boolean;
  includeEstimatedPolicyBalance: boolean;
  includePurposeSummary: boolean;
  includeLumpSumReport: boolean;
  includeStewardship: boolean;
  includeFutureMe: boolean;
  includeFamilyForum: boolean;
  includeWhyMatters: boolean;
  includePaymentLedger: boolean;
  notesText: string;
}

/** A recorded real-world payment, as shown in the optional tracking ledger. */
export interface PaymentLedgerRow {
  payment_number: number;
  status: string;
  planned_date: string | null;
  planned_amount: number | null;
  actual_amount: number | null;
  actual_date: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  late_fee: number;
  nsf_fee: number;
  fee_treatment: string;
  note: string | null;
}

const LEDGER_STATUS_LABEL: Record<string, string> = {
  paid_as_planned: 'Paid as planned',
  paid_late: 'Paid late',
  partial: 'Partial payment',
  extra: 'Extra payment',
  skipped: 'Missed / skipped',
};

function fmtPct(decimal: number): string {
  return (decimal * 100).toFixed(2) + '%';
}

/**
 * "Why This Loan Commitment Matters" — appended as the final page of every
 * generated report so the philosophical purpose travels with the document.
 */
const WHY_COMMITMENT_MATTERS_HTML = `
  <div class="page-break"></div>
  <h2 style="text-align:center; border-bottom:none; font-size:14pt;">Why This Loan Commitment Matters</h2>
  <div style="font-size:10pt; line-height:1.55; max-width:560pt; margin:0 auto;">
    <p style="margin-bottom:8pt;">The long-term strength of a family financing strategy depends less on interest rates and more on behavior.</p>
    <p style="margin-bottom:8pt;">Most policy loans fail because the borrower&mdash;often the same person as the policy owner&mdash;underestimates the importance of repayment discipline and lacks a clear structure for follow-through and accountability.</p>
    <p style="margin-bottom:8pt;">R. Nelson Nash, creator of the Infinite Banking Concept&trade;, often reminded practitioners not to &ldquo;steal the peas.&rdquo; One of the practical ways to apply that principle is to finish your loan schedule.</p>
    <p style="margin-bottom:8pt;"><strong>Before you can finish a loan schedule, you must first create one.</strong></p>
    <p style="margin-bottom:8pt;">This Commitment Agreement transforms an informal intention into a structured commitment. By documenting your repayment plan and committing to specific actions, you create clarity, accountability, and a measurable path forward.</p>
    <p style="margin-bottom:4pt;">Consistent repayment behavior can help:</p>
    <ul style="margin:0 0 8pt 18pt;">
      <li>Reduce the likelihood of loan drift</li>
      <li>Maintain and often improve policy flexibility</li>
      <li>Increase financial confidence</li>
      <li>Strengthen stewardship habits</li>
      <li>Support the long-term success of your family financing strategy</li>
    </ul>
    <p style="margin-bottom:8pt;">In many cases, families choose to repay their policy loans at a higher &ldquo;intended loan rate&rdquo; than the actual policy loan cost. The difference creates what we call <strong>Family Financing Margin&trade;</strong>.</p>
    <p style="margin-bottom:8pt;">Nelson Nash taught that this recaptured financial energy should not be consumed. Instead, it should be redirected back into your family financing strategy whenever possible.</p>
    <p style="margin-bottom:4pt;">This may include:</p>
    <ul style="margin:0 0 8pt 18pt;">
      <li>Additional premium deposits</li>
      <li>Funding another policy within the family system</li>
      <li>Expanding future banking opportunities</li>
      <li>Strengthening family capital reserves</li>
    </ul>
    <p style="margin-bottom:8pt;">Your reward for responsible stewardship is not consumption. Your reward is greater control, greater flexibility, and a stronger system for future generations.</p>
    <p style="margin-bottom:8pt;">Consider discussing this commitment and repayment strategy during your next <strong>Family Financial Forum&trade;</strong>. These conversations help family members understand the purpose behind the commitment, reinforce accountability, and pass stewardship principles from one generation to the next.</p>
    <p style="margin-bottom:4pt;">The goal is not simply to repay a loan.</p>
    <p style="font-weight:600;">The goal is to strengthen the family system that future generations will inherit.</p>
  </div>
`;

/**
 * Interpolate the estimated policy loan balance at an arbitrary date by
 * walking the payment-event ledger and linearly interpolating between the
 * two surrounding events. Returns 0 once the policy loan is paid off.
 */
function interpolatePolicyBalance(
  payments: { date: string; totalIndebtednessAfterEvent: number }[],
  isoDate: string,
): number {
  if (!payments.length) return 0;
  const t = Date.parse(isoDate);
  if (Number.isNaN(t)) return 0;
  const first = Date.parse(payments[0].date);
  if (t <= first) return Math.max(0, payments[0].totalIndebtednessAfterEvent);
  for (let i = 0; i < payments.length - 1; i++) {
    const a = payments[i];
    const b = payments[i + 1];
    const ta = Date.parse(a.date);
    const tb = Date.parse(b.date);
    if (t >= ta && t <= tb) {
      const span = Math.max(1, tb - ta);
      const ratio = (t - ta) / span;
      const bal = a.totalIndebtednessAfterEvent + ratio * (b.totalIndebtednessAfterEvent - a.totalIndebtednessAfterEvent);
      return Math.max(0, bal);
    }
  }
  return Math.max(0, payments[payments.length - 1].totalIndebtednessAfterEvent);
}

const frequencyLabels: Record<string, string> = {
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  semimonthly: 'Semi-Monthly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannual: 'Semi-Annual',
  annual: 'Annual',
  endofterm: 'End of Term',
};

function buildBalanceChartSvg(
  combined: CombinedScenarioResult,
  fmt: (n: number) => string,
): string {
  // Use behavioral curve: only payment-event balances, no intra-period spikes
  const ledger = combined.actualPolicyLoan.ledger;
  const paymentPoints = ledger.filter(e => e.eventType === 'payment');
  if (paymentPoints.length === 0) return '';

  const standardSched = combined.standardLoanComparison.amortizationSchedule;

  // Determine policy payoff index (in the payment-points list)
  let payoffIdx = -1;
  for (let i = 0; i < paymentPoints.length; i++) {
    if (paymentPoints[i].totalIndebtednessAfterEvent < 0.5) { payoffIdx = i; break; }
  }
  const stdBalAtPayoff =
    payoffIdx >= 0 && payoffIdx < standardSched.length
      ? standardSched[payoffIdx].remainingBalance
      : 0;

  // Build paired series across the full horizon
  const fullLen = Math.max(paymentPoints.length, standardSched.length);
  const series = Array.from({ length: fullLen }, (_, i) => {
    const policyRow = i < paymentPoints.length ? paymentPoints[i] : null;
    const stdRow = i < standardSched.length ? standardSched[i] : null;
    let pBal = policyRow ? Math.max(0, policyRow.totalIndebtednessAfterEvent) : 0;
    if (pBal < 0.5) pBal = 0;
    const sBal = stdRow ? Math.max(0, stdRow.remainingBalance) : 0;
    const margin = payoffIdx >= 0 && i >= payoffIdx
      ? Math.max(0, stdBalAtPayoff - sBal)
      : null;
    return {
      date: policyRow?.date ?? stdRow?.paymentDate ?? '',
      policy: pBal,
      standard: sBal,
      margin,
    };
  });

  // Sample to ~40 points
  const step = Math.max(1, Math.floor(series.length / 40));
  const points = series.filter((_, i) => i % step === 0 || i === series.length - 1);

  const maxBalance = Math.max(
    ...points.map(p => Math.max(p.policy, p.standard, p.margin ?? 0)),
  );
  if (maxBalance <= 0) return '';

  const width = 980;
  const height = 380;
  const padLeft = 70;
  const padRight = 20;
  const padTop = 36;
  const padBottom = 50;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  const xScale = (i: number) => padLeft + (i / (points.length - 1)) * chartW;
  const yScale = (v: number) => padTop + chartH - (v / maxBalance) * chartH;

  const buildPath = (vals: (number | null)[]) => {
    let d = '';
    let started = false;
    vals.forEach((v, i) => {
      if (v == null) { started = false; return; }
      const x = xScale(i);
      const y = yScale(v);
      d += `${!started ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `;
      started = true;
    });
    return d.trim();
  };

  const policyPath = buildPath(points.map(p => p.policy));
  const standardPath = buildPath(points.map(p => p.standard));
  const marginPath = buildPath(points.map(p => p.margin));
  const areaD = policyPath
    ? policyPath + ` L${xScale(points.length - 1).toFixed(1)},${(padTop + chartH).toFixed(1)} L${padLeft},${(padTop + chartH).toFixed(1)} Z`
    : '';

  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const val = (maxBalance / 4) * i;
    return { val, y: yScale(val) };
  });

  const xTicks = [0, Math.floor(points.length * 0.25), Math.floor(points.length * 0.5), Math.floor(points.length * 0.75), points.length - 1]
    .filter((v, i, a) => a.indexOf(v) === i)
    .map(i => ({ label: points[i]?.date?.slice(0, 7) || '', x: xScale(i) }));

  const legend = `
    <g font-size="9" font-family="sans-serif">
      <rect x="${padLeft}" y="8" width="14" height="3" fill="#dc2626"/>
      <text x="${padLeft + 20}" y="12" fill="#333">Policy Loan Balance</text>
      <rect x="${padLeft + 170}" y="8" width="14" height="3" fill="#2563eb"/>
      <text x="${padLeft + 190}" y="12" fill="#333">Intended Loan Balance</text>
      <rect x="${padLeft + 350}" y="8" width="14" height="3" fill="#16a34a"/>
      <text x="${padLeft + 370}" y="12" fill="#333">Family Financing Margin™ Growth</text>
    </g>
  `;

  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" style="width:100%; max-width:980px; height:auto;">
      ${legend}
      <!-- Grid lines -->
      ${yTicks.map(t => `<line x1="${padLeft}" y1="${t.y}" x2="${width - padRight}" y2="${t.y}" stroke="#e5e5e5" stroke-width="1"/>`).join('')}
      <!-- Policy area -->
      ${areaD ? `<path d="${areaD}" fill="rgba(220,38,38,0.08)" />` : ''}
      <!-- Intended loan line -->
      ${standardPath ? `<path d="${standardPath}" fill="none" stroke="#2563eb" stroke-width="2" stroke-linejoin="round" stroke-dasharray="6 3" />` : ''}
      <!-- Policy loan line -->
      ${policyPath ? `<path d="${policyPath}" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linejoin="round" />` : ''}
      <!-- Family Financing Margin growth -->
      ${marginPath ? `<path d="${marginPath}" fill="none" stroke="#16a34a" stroke-width="2.25" stroke-linejoin="round" stroke-dasharray="4 2" />` : ''}
      <!-- Y labels -->
      ${yTicks.map(t => `<text x="${padLeft - 6}" y="${t.y + 3}" text-anchor="end" font-size="8" fill="#666">$${(t.val / 1000).toFixed(0)}k</text>`).join('')}
      <!-- X labels -->
      ${xTicks.map(t => `<text x="${t.x}" y="${height - 10}" text-anchor="middle" font-size="8" fill="#666">${t.label}</text>`).join('')}
      <!-- Axes -->
      <line x1="${padLeft}" y1="${padTop}" x2="${padLeft}" y2="${padTop + chartH}" stroke="#999" stroke-width="1"/>
      <line x1="${padLeft}" y1="${padTop + chartH}" x2="${width - padRight}" y2="${padTop + chartH}" stroke="#999" stroke-width="1"/>
    </svg>
  `;
}

function buildPdfHtml(
  loanSetup: LoanSetupData,
  combined: CombinedScenarioResult,
  policyLoanInput: Omit<PolicyLoanInput, "payments" | "projectionEndDate">,
  repaymentStrategy: PlannedRepaymentStrategyInput,
  policyOwner: string,
  loanBorrower: string,
  advisorName: string,
  options: ReportOptions,
  jurisdiction: Jurisdiction,
  dashboard: DashboardSummary,
  solvedPayment?: SolveForPaymentResult | null,
  loanId: string = "",
  stewardship: StewardshipState | null = null,
  futureMe: FutureMeState | null = null,
  exportFilename: string = "",
  purpose: PurposeSelection | null = null,
  lumpSumConfig: LumpSumConfig | null = null,
  lumpSumCombined: CombinedScenarioResult | null = null,
  lumpSumDashboard: DashboardSummary | null = null,
  paymentLedger: PaymentLedgerRow[] = [],
): string {
  const { fmt } = createFormatter(jurisdiction);
  const isUSA = jurisdiction === 'usa';

  const buildFn = isUSA ? buildCommitmentPageDataUS : buildCommitmentPageData;
  const commitmentData = (buildFn as (p: unknown) => ReturnType<typeof buildCommitmentPageData>)({
    clientName: loanBorrower || 'N/A',
    advisorName: advisorName || 'N/A',
    policyNumber: loanSetup.policyNumber,
    loanPurpose: loanSetup.loanTitle || 'N/A',
    policyLoanInput,
    repaymentStrategy,
    combined,
  });

  const payments = combined.plannedPayments;
  // Quoted in the summary so it can never disagree with the schedule below it.
  const lastScheduledPayment = finalScheduledPayment(payments);
  const amortization = combined.standardLoanComparison.amortizationSchedule;
  const carrierLine = isUSA ? `<div><strong>Carrier:</strong> ${e(usaCarrierLabels[loanSetup.usaCarrier])}</div>` : '';

  // Pre-compute interpolated policy balance per amortization row and the
  // "margin recapture" growth once the policy is paid off.
  const policyPayments = combined.actualPolicyLoan.ledger
    .filter(ev => ev.eventType === 'payment')
    .map(ev => ({ date: ev.date, totalIndebtednessAfterEvent: ev.totalIndebtednessAfterEvent }));
  const lastPolicyDate = policyPayments.length
    ? policyPayments[policyPayments.length - 1].date
    : null;
  // Standard balance at the moment of policy payoff (used as the recapture baseline)
  let stdBalAtPayoff = 0;
  if (lastPolicyDate) {
    const target = Date.parse(lastPolicyDate);
    let closest = amortization[0];
    let closestDelta = Number.POSITIVE_INFINITY;
    for (const row of amortization) {
      const d = Math.abs(Date.parse(row.paymentDate) - target);
      if (d < closestDelta) { closestDelta = d; closest = row; }
    }
    stdBalAtPayoff = closest?.remainingBalance ?? 0;
  }

  const commitmentRows = payments.map((p, i) => `
    <tr class="commit-row">
      <td class="num">${i + 1}</td>
      <td class="date">${e(p.date)}</td>
      <td class="right amt">${fmt(p.amount)}</td>
      <td class="center chk">☐</td>
      <td class="notes-line"></td>
    </tr>
  `).join('');

  const includePolicyCol = options.includeEstimatedPolicyBalance;
  const standardRows = amortization.map(r => {
    let policyCell = '';
    if (includePolicyCol) {
      const policyBal = interpolatePolicyBalance(policyPayments, r.paymentDate);
      if (policyBal > 0.5) {
        policyCell = `<td class="right" style="color:#dc2626;font-weight:600;">${fmt(policyBal)}</td>`;
      } else {
        const recapture = Math.max(0, stdBalAtPayoff - r.remainingBalance);
        policyCell = `<td class="right" style="color:#16a34a;font-weight:600;">+${fmt(recapture)}</td>`;
      }
    }
    return `
    <tr>
      <td>${r.paymentNumber}</td>
      <td>${e(r.paymentDate)}</td>
      <td class="right">${fmt(r.paymentAmount)}</td>
      <td class="right">${fmt(r.interestPortion)}</td>
      <td class="right">${fmt(r.principalPortion)}</td>
      <td class="right">${fmt(r.remainingBalance)}</td>
      ${policyCell}
    </tr>
  `;
  }).join('');

  const disclaimer = isUSA
    ? "This document is a planning tool only. Actual policy loan interest, dividend treatment, and loan mechanics may vary between life insurance companies and policy forms. Loan timing, payment timing, and other variables may affect results. All charts, schedules, and projections assume the policy loan interest rate remains constant unless a stress test is specifically included. If the policy loan rate changes, the remaining schedule should be recalculated. These estimates are designed to encourage intentionality and disciplined loan repayment as part of your family financing strategy."
    : commitmentData.signatureDisclaimer;

  const signatories = [
    { label: 'Policy Owner', name: policyOwner },
    { label: 'Loan Borrower', name: loanBorrower },
    ...(options.includeAdvisor ? [{ label: 'Advisor', name: advisorName }] : []),
  ];
  const sigGridClass = signatories.length === 3 ? 'grid3-sig' : 'grid2-sig';

  // ---------- Future Me Letter section ----------
  const futureMeSection = (() => {
    const answers = futureMe?.answers ?? FUTURE_ME_QUESTIONS.map(() => "");
    const blankLines = `
      <div style="border-bottom:1px solid #333; height:18pt;"></div>
      <div style="border-bottom:1px solid #333; height:18pt; margin-top:4pt;"></div>
      <div style="border-bottom:1px solid #333; height:18pt; margin-top:4pt;"></div>
    `;
    const blocks = FUTURE_ME_QUESTIONS.map((q, i) => {
      const ans = (answers[i] || "").trim();
      const body = ans
        ? `<p style="font-size:10pt; line-height:1.5; white-space:pre-wrap; margin-top:4pt;">${e(ans)}</p>`
        : `<div style="margin-top:6pt;">${blankLines}</div>`;
      return `
        <div style="margin-bottom:14pt;">
          <p style="font-weight:600; font-size:10pt;">${i + 1}. ${e(q)}</p>
          ${body}
        </div>
      `;
    }).join("");
    return `
      <div class="page-break"></div>
      <h2>Future Me Letter</h2>
      <p style="font-size:8.5pt; color:#666; margin-bottom:10pt;">
        A short letter from you, today, to your future self — to remember why this loan
        was taken and what following through means for your family.
      </p>
      ${blocks}
    `;
  })();

  // ---------- Family Banking Stewardship Assessment section ----------
  const stewardshipSection = (() => {
    if (!stewardship || !stewardshipCompleted(stewardship)) return "";
    const score = stewardshipScore(stewardship);
    const rating = stewardshipRating(score);
    const sections: Array<"A" | "B" | "C" | "D" | "E"> = ["A", "B", "C", "D", "E"];
    const sectionBlocks = sections.map((sec) => {
      const items = STEWARDSHIP_ITEMS.filter((it) => it.section === sec);
      const rows = items.map((it) => {
        const checked = it.kind === "auto"
          ? stewardship.auto[it.key as keyof StewardshipAuto] === true
          : stewardship.manual[it.key as keyof StewardshipManual] === true;
        const mark = checked ? "☑" : "☐";
        const kindLabel = it.kind === "auto" ? "Auto · " : "";
        return `<tr>
          <td style="width:24pt; text-align:center;">${mark}</td>
          <td>${e(it.label)}</td>
          <td style="width:60pt; text-align:right; font-size:8.5pt; color:#666;">${kindLabel}${it.points} pts</td>
        </tr>`;
      }).join("");
      return `
        <p style="font-weight:600; font-size:10pt; margin:8pt 0 2pt;">${e(SECTION_TITLES[sec])}</p>
        <table style="margin-bottom:4pt;"><tbody>${rows}</tbody></table>
      `;
    }).join("");
    return `
      <h2>Family Banking Stewardship™ Assessment</h2>
      <p style="font-size:9pt; margin-bottom:6pt;">
        Score: <strong>${score}/100</strong> &nbsp;·&nbsp; Rating: <strong>${e(rating)}</strong>
      </p>
      ${sectionBlocks}
      <p style="font-size:8pt; color:#666; font-style:italic;">
        This rating reflects self-reported commitment at the time of borrowing.
        It does not affect the loan calculations and is intended to support
        reflection and family conversation.
      </p>
    `;
  })();

  // ---------- Family Banking Stewardship Reminders / Family Financial Forum seed ----------
  const familyForumSection = `
    <h2>Family Banking Stewardship™ Reminders</h2>
    <p style="font-size:10pt; line-height:1.5;">
      Consider bringing this Family Banking Commitment Agreement™ to your next
      Family Financial Forum™ &mdash; your annual or semi-annual family financial
      discussion. Use it to review the loan purpose, repayment plan, Family
      Financing Margin™, and stewardship commitments with the family members
      affected by this decision.
    </p>
  `;

  // ---------- Loan identification header ----------
  const loanIdHeader = `
    <div class="grid2" style="border-top:1px solid #ccc; border-bottom:1px solid #ccc; padding:6pt 0; margin-bottom:10pt;">
      <div><strong>Loan ID:</strong> <span style="font-family:Menlo,Consolas,monospace;">${e(loanId || "FL-XX-0000")}</span></div>
      <div><strong>Loan Name:</strong> ${e(loanSetup.loanTitle || "—")}</div>
    </div>
  `;

  // ---------- Loan Purpose & Commitment Summary ----------
  const purposeSection = (() => {
    if (!options.includePurposeSummary) return '';
    const hasPurpose = purpose && purpose.categoryId && purpose.specificId;
    const purposeText = hasPurpose ? formatPurpose(purpose) : '';
    const cat = hasPurpose ? findCategory(purpose!.categoryId) : null;
    const note = cat?.note ?? '';
    return `
      <h2>Loan Purpose &amp; Commitment Summary</h2>
      <div class="grid2" style="margin-bottom:8pt;">
        <div><strong>Stated Purpose:</strong> ${hasPurpose ? e(purposeText) : '<span style="color:#999;">Not specified</span>'}</div>
        <div><strong>Repayment Frequency:</strong> ${e(frequencyLabels[commitmentData.paymentFrequency])}</div>
        <div><strong>Target Payment:</strong> ${fmt(commitmentData.targetPaymentAmount)}</div>
        <div><strong>Number of Payments:</strong> ${commitmentData.targetNumberOfPayments}</div>
        <div><strong>Projected Payoff:</strong> ${e(commitmentData.projectedFinalPaymentDate)}</div>
        <div><strong>Family Financing Margin&trade;:</strong> <span style="color:#16a34a;font-weight:600;">${fmt(commitmentData.additionalIntendedFinancingMargin)}</span></div>
      </div>
      ${note ? `<p style="font-size:9pt; line-height:1.5; color:#444; border-left:3px solid #16a34a; padding:6pt 10pt; background:#f0fdf4; margin-bottom:6pt;">${e(note)}</p>` : ''}
      <p style="font-size:8.5pt; color:#666; font-style:italic;">
        Defining the purpose anchors this borrowing decision to a real outcome and creates a reference point
        for stewardship and future family conversations.
      </p>
    `;
  })();

  // ---------- Lump Sum Scenario Report ----------
  const lumpSumSection = (() => {
    if (!options.includeLumpSumReport) return '';
    const scenarios = lumpSumConfig?.scenarios ?? [];
    const recurring = lumpSumConfig?.recurring;
    const recurringActive = recurring?.enabled && (recurring?.amount ?? 0) > 0;
    if (scenarios.length === 0 && !recurringActive) return '';

    const scenarioRows = scenarios
      .filter(s => (s.amount ?? 0) > 0)
      .map(s => `
        <tr>
          <td>${e(s.source === 'other' ? (s.otherDescription || 'Other') : s.source)}</td>
          <td>${e(s.source)}</td>
          <td>${e(s.knowsDate ? s.date : (s.estimatedMonthOffset ? `~ month ${s.estimatedMonthOffset}` : '—'))}</td>
          <td class="right">${fmt(s.amount)}</td>
        </tr>
      `).join('');

    const recurringBlock = recurringActive ? `
      <p style="font-weight:600; margin-top:8pt;">Recurring Lump Sum Strategy</p>
      <div class="grid2" style="margin-bottom:6pt;">
        <div><strong>Amount:</strong> ${fmt(recurring!.amount)} per occurrence</div>
        <div><strong>Frequency:</strong> ${e(recurring!.frequency)}</div>
        <div><strong>Source:</strong> ${e(recurring!.source || '—')}</div>
        <div><strong>Start:</strong> ${e(recurring!.startDate || 'Loan start')}</div>
        <div><strong>End:</strong> ${
          recurring!.endMode === 'occurrences'
            ? `${recurring!.occurrences} occurrences`
            : (recurring!.endDate || '—')
        }</div>
      </div>
    ` : '';

    const marginChange = lumpSumDashboard
      ? summariseMarginChange(
          dashboard.additionalIntendedFinancingMargin,
          lumpSumDashboard.additionalIntendedFinancingMargin,
        )
      : null;

    const compareBlock = lumpSumDashboard && marginChange ? `
      <p style="font-weight:600; margin-top:8pt;">Estimated Impact vs. Base Plan</p>
      <div class="recapture-grid">
        <div class="recapture-box">
          <div class="label">Base Policy Payments</div>
          <div class="value">${dashboard.policyLoanPaymentCount}</div>
        </div>
        <div class="recapture-box">
          <div class="label">With Lump Sums</div>
          <div class="value green">${lumpSumDashboard.policyLoanPaymentCount}</div>
        </div>
        <div class="recapture-box">
          <div class="label">Payments Saved</div>
          <div class="value green">${Math.max(0, dashboard.policyLoanPaymentCount - lumpSumDashboard.policyLoanPaymentCount)}</div>
        </div>
        <div class="recapture-box">
          <div class="label">Base Family Financing Margin&trade;</div>
          <div class="value">${fmt(dashboard.additionalIntendedFinancingMargin)}</div>
        </div>
        <div class="recapture-box">
          <div class="label">With Lump Sums</div>
          <div class="value${marginChange.direction === 'reduced' ? '' : ' green'}">${fmt(lumpSumDashboard.additionalIntendedFinancingMargin)}</div>
        </div>
        <div class="recapture-box">
          <div class="label">${e(marginChange.label)}</div>
          <div class="value${marginChange.direction === 'gained' ? ' green' : ''}">${fmt(marginChange.delta)}</div>
        </div>
      </div>
      ${marginChange.direction === 'reduced' ? `
      <p style="font-size:8pt; color:#666; margin-top:6pt;">
        Retiring the policy loan sooner leaves fewer periods for the repayment overage to accumulate,
        so the estimated Family Financing Margin&trade; is lower under this lump-sum plan.
      </p>` : ''}
    ` : '';

    return `
      <div class="page-break"></div>
      <h2>Lump Sum Scenario Report</h2>
      <p style="font-size:8.5pt; color:#666; margin-bottom:8pt;">
        Estimated impact of one-time and recurring lump-sum principal reductions on the policy loan timeline
        and Family Financing Margin&trade;.
      </p>
      ${scenarioRows ? `
        <p style="font-weight:600;">Planned One-Time Lump Sums</p>
        <table>
          <thead><tr><th>Label</th><th>Source</th><th>Date</th><th class="right">Amount</th></tr></thead>
          <tbody>${scenarioRows}</tbody>
        </table>
      ` : ''}
      ${recurringBlock}
      ${compareBlock}
    `;
  })();

  // Build optional sections
  const notesSection = options.includeNotes && options.notesText.trim() ? `
    <div style="border:1.5px solid #2563eb; border-radius:4pt; padding:10pt 12pt; margin:14pt 0; background:#eff6ff;">
      <p style="font-weight:700; font-size:10pt; color:#1e40af; margin-bottom:6pt;">📝 Loan Notes & Commitment Details</p>
      <p style="font-size:9.5pt; line-height:1.6; white-space:pre-wrap;">${e(options.notesText)}</p>
    </div>
  ` : '';

  const chartSection = options.includeCharts ? `
    <div class="page-break"></div>
    <h2>Estimated Loan Balance Trajectory</h2>
    <p style="font-size:8.5pt; color:#666; margin-bottom:8pt;">
      Estimated projection comparing the Policy Loan balance, the Intended Loan balance, and the
      Family Financing Margin&trade; growth that begins rebuilding after the policy loan is repaid.
    </p>
    <div class="chart-wrap">${buildBalanceChartSvg(combined, fmt)}</div>
  ` : '';

  const policyVolPct = (dashboard.policyLoanInterestVolume * 100).toFixed(2);
  const standardVolPct = (dashboard.standardLoanInterestVolume * 100).toFixed(2);

  const payoffSummarySection = options.includePayoffSummary ? `
    <h2>Estimated Payoff Summary</h2>
    <div class="recapture-grid">
      <div class="recapture-box">
        <div class="label">Committed Loan Schedule</div>
        <div class="value">${dashboard.standardLoanPaymentCount} payments</div>
      </div>
      <div class="recapture-box">
        <div class="label">Estimated Policy Loan Repaid</div>
        <div class="value green">${dashboard.policyLoanPaymentCount} payments</div>
      </div>
      ${dashboard.earlyPayoffPeriods > 0 ? `
      <div class="recapture-box">
        <div class="label">Remaining Payments<br/>(Redirect into System)</div>
        <div class="value green">${dashboard.earlyPayoffPeriods} payments</div>
      </div>
      ` : ''}
    </div>
  ` : '';

  const financingMarginSection = options.includeFinancingMargin ? `
    <h2>Estimated Additional Financing Margin</h2>
    <div class="recapture-grid">
      <div class="recapture-box">
        <div class="label">Additional Financing Margin Estimate</div>
        <div class="value green">${fmt(dashboard.additionalIntendedFinancingMargin)}</div>
      </div>
      <div class="recapture-box">
        <div class="label">Suggested Premium Redirect Estimate</div>
        <div class="value green">${fmt(dashboard.suggestedExtraToFamilySystemPremiums)}</div>
      </div>
      <div class="recapture-box">
        <div class="label">Target Payment Per Period</div>
        <div class="value">${fmt(dashboard.targetPaymentPerPeriod)}</div>
      </div>
    </div>
  ` : '';

  const interestVolumeSection = options.includeInterestVolume ? `
    <h2>Estimated Interest Volume Comparison</h2>
    <p style="font-size:8.5pt; color:#666; margin-bottom:8pt;">
      Interest volume shows how much of your total payments go toward interest vs. principal. Lower is better.
    </p>
    <div class="recapture-grid">
      <div class="recapture-box">
        <div class="label">Policy Loan<br/>Interest Volume Estimate</div>
        <div class="value">${policyVolPct}%</div>
        <div style="font-size:7.5pt; color:#666; margin-top:2pt;">${fmt(dashboard.policyLoanTotalInterest)} of ${fmt(dashboard.totalPaymentsApplied)} total</div>
      </div>
      <div class="recapture-box">
        <div class="label">Intended Loan<br/>Interest Volume Estimate</div>
        <div class="value">${standardVolPct}%</div>
        <div style="font-size:7.5pt; color:#666; margin-top:2pt;">${fmt(dashboard.bankLoanTotalInterest)} of ${fmt(dashboard.standardLoanTotalPayments)} total</div>
      </div>
      <div class="recapture-box">
        <div class="label">Policy Loan<br/>Total Interest Estimate</div>
        <div class="value">${fmt(dashboard.policyLoanTotalInterest)}</div>
      </div>
    </div>
  ` : '';

  const commitmentScheduleSection = options.includeCommitmentSchedule ? `
    <div class="page-break"></div>
    <h2>Family Banking Commitment Schedule&trade; — ${payments.length} Payments</h2>
    <p style="font-size:8pt;color:#666;margin-bottom:6pt;">
      Use the full-width line on each row to handwrite the date paid, confirmation #, or any notes.
    </p>
    <table>
      <thead>
        <tr>
          <th class="num">#</th>
          <th class="date">Due Date</th>
          <th class="right amt">Amount</th>
          <th class="center chk">Paid</th>
          <th class="notes-line">Date Paid / Notes</th>
        </tr>
      </thead>
      <tbody>
        ${commitmentRows}
      </tbody>
    </table>
  ` : '';

  const amortizationSection = options.includeAmortization ? `
    <h2>Intended Loan Amortization Comparison — ${amortization.length} Payments</h2>
    <p style="font-size:8.5pt; color:#666; margin-bottom:8pt;">
      This schedule shows what the same borrowing would look like under an intended amortized loan at ${fmtPct(commitmentData.chosenRepaymentRate)}.
      Use this to confirm remaining balances if consolidating.
    </p>
    ${includePolicyCol ? `<p style="font-size:8pt; color:#666; margin-bottom:6pt;">
      <span style="color:#dc2626;font-weight:600;">Red</span> = Estimated Policy Loan Balance (still declining).
      <span style="color:#16a34a;font-weight:600;">Green</span> = Family Financing Margin&trade; rebuilding after policy payoff.
    </p>` : ''}
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Date</th>
          <th class="right">Payment</th>
          <th class="right">Interest</th>
          <th class="right">Principal</th>
          <th class="right">Balance</th>
          ${includePolicyCol ? `<th class="right">Est. Policy<br/>Loan Balance*</th>` : ''}
        </tr>
      </thead>
      <tbody>
        ${standardRows}
      </tbody>
    </table>
    ${includePolicyCol ? `<p style="font-size:7.5pt; color:#666; margin-top:4pt; font-style:italic;">
      * Estimated and interpolated to the intended-loan payment date from the policy-loan ledger.
    </p>` : ''}
  ` : '';

  // Optional ledger of what actually happened, including fees and references.
  const ledgerRows = [...paymentLedger].sort((a, b) => a.payment_number - b.payment_number);
  const ledgerFees = ledgerRows.reduce((s, r) => s + (r.late_fee || 0) + (r.nsf_fee || 0), 0);
  const ledgerPaid = ledgerRows.reduce((s, r) => s + (r.actual_amount ?? 0), 0);
  const paymentLedgerSection = options.includePaymentLedger && ledgerRows.length > 0 ? `
    <h2>Actual Payment History &amp; Tracking Ledger</h2>
    <p style="font-size:8.5pt; color:#666; margin-bottom:8pt;">
      Recorded repayment activity against this commitment, including payment method, reference numbers,
      late charges, NSF charges, and notes.
    </p>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Due Date</th>
          <th class="right">Planned</th>
          <th>Status</th>
          <th class="right">Paid</th>
          <th>Date Paid</th>
          <th>Method / Ref</th>
          <th class="right">Late Fee</th>
          <th class="right">NSF Fee</th>
          <th>Fee Handling</th>
          <th>Note</th>
        </tr>
      </thead>
      <tbody>
        ${ledgerRows.map(r => `
          <tr>
            <td>${r.payment_number}</td>
            <td>${e(r.planned_date ?? '')}</td>
            <td class="right">${r.planned_amount != null ? fmt(r.planned_amount) : '—'}</td>
            <td>${e(LEDGER_STATUS_LABEL[r.status] ?? r.status)}</td>
            <td class="right">${r.actual_amount != null ? fmt(r.actual_amount) : '—'}</td>
            <td>${e(r.actual_date ?? '')}</td>
            <td>${e([r.payment_method, r.payment_reference].filter(Boolean).join(' ')) || '—'}</td>
            <td class="right">${r.late_fee ? fmt(r.late_fee) : '—'}</td>
            <td class="right">${r.nsf_fee ? fmt(r.nsf_fee) : '—'}</td>
            <td>${(r.late_fee || r.nsf_fee) ? (r.fee_treatment === 'capitalized' ? 'Added to balance' : 'Paid with payment') : '—'}</td>
            <td>${e(r.note ?? '')}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
    <p style="font-size:8.5pt; margin-top:6pt;">
      <strong>Total recorded payments:</strong> ${fmt(ledgerPaid)} &nbsp;&nbsp;
      <strong>Total fees assessed:</strong> ${fmt(ledgerFees)}
    </p>
  ` : '';



  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${e(exportFilename || "Family Banking Commitment Agreement")}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page {
      margin: 0.5in 0.6in 0.65in 0.6in;
      @bottom-right {
        content: "Page " counter(page) " of " counter(pages);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 8pt;
        color: #666;
      }
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 10pt;
      color: #1a1a2e;
      padding: 0.5in 0.6in;
      line-height: 1.4;
    }
    h1 { font-size: 16pt; text-align: center; margin-bottom: 2pt; }
    h2 { font-size: 11pt; margin: 12pt 0 6pt; border-bottom: 1px solid #ccc; padding-bottom: 3pt; }
    .subtitle { text-align: center; font-size: 9pt; color: #666; margin-bottom: 16pt; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 3pt 16pt; margin-bottom: 10pt; }
    .grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 3pt 16pt; margin-bottom: 10pt; }
    .grid2-sig { display: grid; grid-template-columns: 1fr 1fr; gap: 30pt; margin-bottom: 16pt; }
    .grid3-sig { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20pt; margin-bottom: 16pt; }
    strong { font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 12pt; font-size: 9pt; }
    th, td { padding: 3pt 4pt; text-align: left; border-bottom: 1px solid #e5e5e5; }
    th { border-bottom: 2px solid #999; font-weight: 600; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.3pt; }
    td.right, th.right { text-align: right; }
    td.center, th.center { text-align: center; }
    .sig-block { font-size: 10pt; }
    .sig-block p { margin-bottom: 14pt; }
    .sig-line { display: inline-block; min-width: 280pt; border-bottom: 1px solid #333; }
    .disclaimer { font-size: 7.5pt; color: #666; border-top: 1px solid #ccc; padding-top: 8pt; margin-top: 16pt; line-height: 1.5; }
    .page-break { page-break-before: always; }
    .chart-wrap { width: 100%; max-width: 980px; margin: 0 auto; }
    .chart-wrap svg { width: 100% !important; max-width: 980px !important; height: auto !important; }
    .recapture-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8pt; margin-bottom: 12pt; }
    .recapture-box { background: #f5f5f5; padding: 8pt; border-radius: 4pt; text-align: center; }
    .recapture-box .label { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.3pt; color: #666; }
    .recapture-box .value { font-size: 12pt; font-weight: 600; margin-top: 2pt; }
    .green { color: #16a34a; }
    .commitment-box { border: 1.5px solid #1a1a2e; padding: 10pt 12pt; margin: 14pt 0; border-radius: 4pt; }
    /* Commitment row: writable notes line spans the rest of the row */
    table tr.commit-row td { padding-top: 6pt; padding-bottom: 6pt; }
    table td.num, table th.num { width: 22pt; }
    table td.date, table th.date { width: 62pt; }
    table td.amt, table th.amt { width: 70pt; }
    table td.chk, table th.chk { width: 28pt; font-size: 11pt; }
    table td.notes-line { border-bottom: 1px solid #333; height: 22pt; }
    @media print {
      body { padding: 0; }
      .page-break { page-break-before: always; }
      table { page-break-inside: auto; }
      tr    { page-break-inside: avoid; page-break-after: auto; }
      thead { display: table-header-group; }
    }
  </style>
</head>
<body>

  <h1>Family Banking Commitment Agreement™</h1>
  <p class="subtitle">Generated: ${new Date().toLocaleDateString(jurisdiction === 'usa' ? 'en-US' : 'en-CA')}</p>

  ${loanIdHeader}

  <div class="${options.includeAdvisor ? 'grid3' : 'grid2'}">
    <div><strong>Policy Owner:</strong> ${e(policyOwner) || '_______________'}</div>
    <div><strong>Loan Borrower:</strong> ${e(loanBorrower) || '_______________'}</div>
    ${options.includeAdvisor ? `<div><strong>Advisor:</strong> ${e(advisorName) || '_______________'}</div>` : ''}
  </div>

  <div class="grid2" style="border-top:1px solid #ccc; border-bottom:1px solid #ccc; padding:6pt 0;">
    <div><strong>Policy Number:</strong> ${e(loanSetup.policyNumber) || 'N/A'}</div>
    <div><strong>Life Insured:</strong> ${e(loanSetup.lifeInsured) || 'N/A'}</div>
    ${carrierLine}
  </div>

  ${purposeSection}

  <h2>Loan Summary</h2>
  ${loanSetup.hasExistingBalance && loanSetup.consolidateExisting && loanSetup.existingBalance > 0 ? `
  <div style="border:2px solid #dc2626; border-radius:4pt; padding:8pt 10pt; margin-bottom:10pt; background:#fef2f2;">
    <div style="font-size:10pt; font-weight:700; color:#dc2626; margin-bottom:4pt;">⚠ CONSOLIDATION APPLIED</div>
    <div class="grid2" style="margin-bottom:0;">
      <div><strong>New Loan Amount:</strong> ${fmt(loanSetup.loanAmount)}</div>
      <div style="color:#dc2626; font-weight:700;">Existing Balance Added: ${fmt(loanSetup.existingBalance)}</div>
      <div style="font-weight:700; font-size:11pt;">Total Consolidated Loan: ${fmt(loanSetup.loanAmount + loanSetup.existingBalance)}</div>
    </div>
  </div>
  ` : ''}
  <div class="grid2">
    <div>Loan Purpose: ${e(commitmentData.loanPurpose)}</div>
    <div><strong>Loan Amount: ${fmt(repaymentStrategy.borrowedAmount)}</strong></div>
    <div>Policy Loan Rate: ${fmtPct(commitmentData.policyLoanAnnualRate)}</div>
    <div>Discipline Rate: ${fmtPct(commitmentData.chosenRepaymentRate)}</div>
    <div>Frequency: ${e(frequencyLabels[commitmentData.paymentFrequency])}</div>
    <div>Target Payment: ${fmt(solvedPayment?.found ? loanSetup.desiredPayment : commitmentData.targetPaymentAmount)}</div>
    <div>Number of Payments: ${commitmentData.targetNumberOfPayments}</div>
    <div>Projected Payoff: ${e(commitmentData.projectedFinalPaymentDate)}</div>
    ${solvedPayment?.found ? `
    <div><strong>Desired Payment:</strong> ${fmt(loanSetup.desiredPayment)}</div>
    <div><strong>Solved Rate:</strong> ${(solvedPayment.solvedRate * 100).toFixed(2)}%</div>
    <div><strong>Extra Principal/Payment:</strong> ${fmt(solvedPayment.extraPrincipal)}</div>
    ${lastScheduledPayment !== null ? `
    <div><strong>Est. Final Payment:</strong> ${fmt(lastScheduledPayment)}</div>` : ''}
    ` : ''}
  </div>

  <h2>Recapture Summary</h2>
  <div class="recapture-grid">
    <div class="recapture-box">
      <div class="label">Policy Interest Cost<br/>(Your Co-Ownership Pool)</div>
      <div class="value">${fmt(commitmentData.actualPolicyLoanInterestCost)}</div>
    </div>
    <div class="recapture-box">
      <div class="label">Additional Financing Margin</div>
      <div class="value green">${fmt(commitmentData.additionalIntendedFinancingMargin)}</div>
    </div>
    <div class="recapture-box">
      <div class="label">Suggested Premium Redirect</div>
      <div class="value green">${fmt(commitmentData.suggestedExtraToFamilySystemPremiums)}</div>
    </div>
  </div>

  ${notesSection}

  ${payoffSummarySection}

  ${financingMarginSection}

  ${interestVolumeSection}

  ${commitmentScheduleSection}

  <div class="commitment-box">
    <p><strong>Borrower's Commitment Statement</strong></p>
    <p style="margin-top:6pt;">I, <strong>${e(loanBorrower) || '_______________'}</strong>, hereby commit to repaying the above-described loan in full accordance with the payment schedule set forth in this document. I understand that each payment is due on or before the date indicated, and I accept full responsibility for ensuring timely repayment. I acknowledge that this commitment is made in good faith and that the funds advanced are sourced from the Policy Owner's life insurance policy loan facility.</p>
  </div>

  <h2>Signatures</h2>
  <div class="${sigGridClass}">
    ${signatories.map(s => `
      <div class="sig-block">
        <p><strong>${e(s.label)}</strong></p>
        <p>Printed Name: ${e(s.name) || '<span class="sig-line">&nbsp;</span>'}</p>
        <p>Signature: <span class="sig-line">&nbsp;</span></p>
        <p>Date: <span class="sig-line">&nbsp;</span></p>
      </div>
    `).join('')}
  </div>

  ${amortizationSection ? `<div class="page-break"></div>${amortizationSection}` : ''}

  ${paymentLedgerSection ? `<div class="page-break"></div>${paymentLedgerSection}` : ''}

  ${chartSection}

  ${lumpSumSection}

  ${options.includeStewardship && stewardshipSection ? `<div class="page-break"></div>${stewardshipSection}` : ''}

  ${options.includeFutureMe ? futureMeSection : ''}

  ${options.includeFamilyForum ? familyForumSection : ''}

  <p class="disclaimer">${e(disclaimer)}</p>

  <p class="disclaimer"><strong>Important Assumption:</strong> All charts, schedules, and projections in this document assume the policy loan interest rate remains constant at ${(policyLoanInput.policyLoanAnnualRate * 100).toFixed(2)}% for the entire duration of the loan. If the policy loan rate changes at any time, the remaining amortization schedule must be recalculated using updated figures and a new commitment schedule generated to reflect the revised terms.</p>

  ${options.includeWhyMatters ? WHY_COMMITMENT_MATTERS_HTML : ''}

</body>
</html>`;
}

export function PrintSchedule({
  loanSetup,
  combined,
  policyLoanInput,
  repaymentStrategy,
  jurisdiction,
  solvedPayment,
  dashboard,
  loanId,
  stewardship,
  futureMe,
  policyOwnerName,
  onPolicyOwnerNameChange,
  loanBorrowerName,
  onLoanBorrowerNameChange,
  purpose = null,
  lumpSumConfig = null,
  lumpSumCombined = null,
  lumpSumDashboard = null,
  onCommitmentGenerated,
  scenarioId = null,
}: PrintScheduleProps) {
  const policyOwner = policyOwnerName;
  const loanBorrower = loanBorrowerName;
  const setPolicyOwner = onPolicyOwnerNameChange;
  const setLoanBorrower = onLoanBorrowerNameChange;
  const [advisorName, setAdvisorName] = useState('');
  const [isThirdParty, setIsThirdParty] = useState(false);
  const [includeAdvisor, setIncludeAdvisor] = useState(false);
  const [governingProvince, setGoverningProvince] = useState('');
  const [governingCountry, setGoverningCountry] = useState('');
  const [relationshipToOwner, setRelationshipToOwner] = useState('');
  const [borrowerAddress, setBorrowerAddress] = useState('');
  const [witnessName, setWitnessName] = useState('');
  const [legal, setLegal] = useState<ThirdPartyLegalOptions>(DEFAULT_THIRD_PARTY_LEGAL);

  // Report options dialog
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [paymentLedger, setPaymentLedger] = useState<PaymentLedgerRow[]>([]);
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    includeCommitmentSchedule: true,
    includeAmortization: true,
    includeAdvisor: false,
    includeCharts: false,
    includeNotes: false,
    includePayoffSummary: true,
    includeFinancingMargin: true,
    includeInterestVolume: true,
    includeEstimatedPolicyBalance: true,
    includePurposeSummary: true,
    includeLumpSumReport: true,
    includeStewardship: true,
    includeFutureMe: true,
    includeFamilyForum: true,
    includeWhyMatters: true,
    includePaymentLedger: false,
    notesText: '',
  });

  // Recorded payment history, loaded only when a saved scenario is open.
  useEffect(() => {
    if (!scenarioId) { setPaymentLedger([]); return; }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("scenario_payment_actuals")
        .select("payment_number,status,planned_date,planned_amount,actual_amount,actual_date,payment_method,payment_reference,late_fee,nsf_fee,fee_treatment,note")
        .eq("scenario_id", scenarioId);
      if (cancelled || error) return;
      setPaymentLedger(
        (data ?? []).map((r) => ({
          payment_number: r.payment_number,
          status: r.status as string,
          planned_date: r.planned_date as string | null,
          planned_amount: r.planned_amount as number | null,
          actual_amount: r.actual_amount as number | null,
          actual_date: r.actual_date as string | null,
          payment_method: (r.payment_method as string | null) ?? null,
          payment_reference: (r.payment_reference as string | null) ?? null,
          late_fee: Number(r.late_fee ?? 0),
          nsf_fee: Number(r.nsf_fee ?? 0),
          fee_treatment: (r.fee_treatment as string | null) ?? 'paid_with_payment',
          note: (r.note as string | null) ?? null,
        }))
      );
    })();
    return () => { cancelled = true; };
  }, [scenarioId]);

  // Sync advisor checkbox with report options
  const updateReportOption = (key: keyof ReportOptions, value: boolean | string) => {
    setReportOptions(prev => ({ ...prev, [key]: value }));
    if (key === 'includeAdvisor') {
      setIncludeAdvisor(value as boolean);
    }
  };

  // Popup-proof: print from a hidden same-page iframe first; only fall back
  // to a new window (and its popup blocker) if the iframe path fails.
  const printDocument = async (html: string, title?: string): Promise<boolean> => {
    const printed = await printHtmlInPage(html, title);
    if (printed) return true;
    const opened = openPrintWindow(html);
    if (!opened) {
      toast.error(
        "Couldn't open the document. Allow popups for this site and try again.",
      );
    }
    return opened;
  };

  const handleOpenReportDialog = () => {
    // Sync advisor state into report options
    setReportOptions(prev => ({ ...prev, includeAdvisor }));
    setShowReportDialog(true);
  };

  const handleGeneratePdf = async () => {
    setShowReportDialog(false);
    // Let the dialog fully unmount (and release its page lock) before the
    // blocking print dialog opens — otherwise the lock cleanup can be
    // interrupted and the page appears frozen afterwards.
    await new Promise((r) => window.setTimeout(r, 50));
    // Reserve the next sequential number for these initials only at export time,
    // so a user can preview many "FL-XX-0001" drafts without burning through IDs.
    const initials = resolveClientInitials(loanBorrower, policyOwner);
    const reservedSeq = reserveNextLoanNumber(initials);
    const finalLoanId = formatLoanId(initials, reservedSeq);
    const totalAmount = (loanSetup.loanAmount || 0) +
      (loanSetup.hasExistingBalance && loanSetup.consolidateExisting ? (loanSetup.existingBalance || 0) : 0);
    const filename = buildExportFilename(finalLoanId, loanSetup.loanDate, totalAmount);

    const html = buildPdfHtml(
      loanSetup, combined, policyLoanInput, repaymentStrategy,
      policyOwner, loanBorrower, advisorName, reportOptions, jurisdiction, dashboard, solvedPayment,
      finalLoanId, stewardship, futureMe, filename,
      purpose, lumpSumConfig, lumpSumCombined, lumpSumDashboard, paymentLedger,
    );
    // Only promote the scenario to "Committed" when a document was actually
    // produced — a failed print must never create a commitment.
    if (await printDocument(html, filename)) {
      onCommitmentGenerated?.();
    }
  };

  // Real, attachable PDF file — the concise agreement + schedule + signatures.
  const handleDownloadPdf = async () => {
    try {
      const initials = resolveClientInitials(loanBorrower, policyOwner);
      const reservedSeq = reserveNextLoanNumber(initials);
      const finalLoanId = formatLoanId(initials, reservedSeq);
      const totalAmount = (loanSetup.loanAmount || 0) +
        (loanSetup.hasExistingBalance && loanSetup.consolidateExisting ? (loanSetup.existingBalance || 0) : 0);
      const model = buildCommitmentDocModel({
        loanId: finalLoanId,
        policyOwner,
        loanBorrower,
        advisorName: includeAdvisor && advisorName.trim() ? advisorName.trim() : undefined,
        jurisdiction: jurisdiction === "canada" ? "canada" : "usa",
        loanAmount: totalAmount,
        annualRatePct: repaymentStrategy.targetAnnualRate * 100,
        paymentFrequency: repaymentStrategy.paymentFrequency,
        loanDate: loanSetup.loanDate,
        startDate: repaymentStrategy.startDate,
        generatedOn: new Date().toISOString().slice(0, 10),
        schedule: combined.plannedPayments.map((p) => ({ date: p.date, amount: p.amount })),
      });
      // jsPDF is heavy — load it only when someone actually downloads.
      const { renderCommitmentPdf } = await import("@/lib/pdf/renderCommitmentPdf");
      renderCommitmentPdf(model).save(model.filename);
      setShowReportDialog(false);
      onCommitmentGenerated?.();
    } catch (e2) {
      console.error("PDF download failed", e2);
      toast.error("Couldn't build the PDF. Please try again.");
    }
  };

  const handleGenerateThirdPartyDoc = () => {
    const html = buildThirdPartyLoanHtml({
      loanSetup, combined, policyLoanInput, repaymentStrategy,
      policyOwner, loanBorrower, advisorName,
      governingProvince, governingCountry, relationshipToOwner, borrowerAddress, witnessName,
      legal,
    });
    void printDocument(html, "Third-Party Loan Document");
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Generate Commitment Schedule</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Policy Owner</Label>
              <Input value={policyOwner} onChange={e => setPolicyOwner(e.target.value)} placeholder="Full Name" />
            </div>
            <div>
              <Label>Loan Borrower</Label>
              <Input value={loanBorrower} onChange={e => setLoanBorrower(e.target.value)} placeholder="Full Name" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="include-advisor"
              checked={includeAdvisor}
              onCheckedChange={(checked) => setIncludeAdvisor(checked === true)}
            />
            <Label htmlFor="include-advisor" className="cursor-pointer text-sm">
              Include Advisor / Representative on schedule
            </Label>
          </div>

          {includeAdvisor && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Advisor / Representative</Label>
                <Input value={advisorName} onChange={e => setAdvisorName(e.target.value)} placeholder="Full Name" />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="third-party"
              checked={isThirdParty}
              onCheckedChange={(checked) => setIsThirdParty(checked === true)}
            />
            <Label htmlFor="third-party" className="cursor-pointer text-sm">
              Borrower is a third party (not the policy owner)
            </Label>
          </div>

          {isThirdParty && (
            <div className="border border-border rounded-lg p-4 space-y-4 bg-muted/30">
              <p className="text-sm font-medium text-foreground">Third-Party Borrower Details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Relationship to Policy Owner</Label>
                  <Input value={relationshipToOwner} onChange={e => setRelationshipToOwner(e.target.value)} placeholder="e.g. Son, Daughter, Business Partner" />
                </div>
                <div>
                  <Label>Borrower Address</Label>
                  <Input value={borrowerAddress} onChange={e => setBorrowerAddress(e.target.value)} placeholder="Full mailing address" />
                </div>
                <div>
                  <Label>Governing {jurisdiction === 'usa' ? 'State' : 'Province / State'}</Label>
                  <Input value={governingProvince} onChange={e => setGoverningProvince(e.target.value)} placeholder={jurisdiction === 'usa' ? 'e.g. California, Texas' : 'e.g. Ontario, California'} />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input value={governingCountry} onChange={e => setGoverningCountry(e.target.value)} placeholder={jurisdiction === 'usa' ? 'United States' : 'e.g. Canada, United States'} />
                </div>
                <div>
                  <Label>Witness Name (Optional)</Label>
                  <Input value={witnessName} onChange={e => setWitnessName(e.target.value)} placeholder="Full Name" />
                </div>
              </div>

              {/* Security, prepayment and notarization terms */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
                <div>
                  <Label>Collateral / Security</Label>
                  <Select
                    value={legal.collateralKind}
                    onValueChange={(v) => setLegal({ ...legal, collateralKind: v as CollateralKind })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unsecured">Unsecured (no collateral)</SelectItem>
                      <SelectItem value="vehicle">Vehicle / equipment collateral</SelectItem>
                      <SelectItem value="other">Other described collateral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prepayment Terms</Label>
                  <Select
                    value={legal.prepaymentKind}
                    onValueChange={(v) => setLegal({ ...legal, prepaymentKind: v as PrepaymentKind })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No prepayment penalty</SelectItem>
                      <SelectItem value="penalty">Prepayment premium (% of principal prepaid)</SelectItem>
                      <SelectItem value="minimum_interest">Minimum interest period</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {legal.collateralKind === 'vehicle' && (
                  <>
                    <div>
                      <Label>Year / Make / Model</Label>
                      <div className="grid grid-cols-3 gap-2">
                        <Input value={legal.vehicleYear} onChange={e => setLegal({ ...legal, vehicleYear: e.target.value })} placeholder="Year" />
                        <Input value={legal.vehicleMake} onChange={e => setLegal({ ...legal, vehicleMake: e.target.value })} placeholder="Make" />
                        <Input value={legal.vehicleModel} onChange={e => setLegal({ ...legal, vehicleModel: e.target.value })} placeholder="Model" />
                      </div>
                    </div>
                    <div>
                      <Label>VIN / Serial Number</Label>
                      <Input value={legal.vehicleVin} onChange={e => setLegal({ ...legal, vehicleVin: e.target.value })} placeholder="VIN" />
                    </div>
                    <div>
                      <Label>Odometer / Hours</Label>
                      <Input value={legal.vehicleMileage} onChange={e => setLegal({ ...legal, vehicleMileage: e.target.value })} placeholder="e.g. 84,300 km" />
                    </div>
                    <div>
                      <Label>Existing Lienholder (Optional)</Label>
                      <Input value={legal.vehicleLienholder} onChange={e => setLegal({ ...legal, vehicleLienholder: e.target.value })} placeholder="None" />
                    </div>
                  </>
                )}

                {legal.collateralKind === 'other' && (
                  <div className="md:col-span-2">
                    <Label>Description of Collateral</Label>
                    <Textarea value={legal.otherCollateral} onChange={e => setLegal({ ...legal, otherCollateral: e.target.value })} placeholder="Describe the pledged property or assets" />
                  </div>
                )}

                {legal.prepaymentKind === 'penalty' && (
                  <div>
                    <Label>Prepayment Premium (%)</Label>
                    <Input value={legal.prepaymentPercent} onChange={e => setLegal({ ...legal, prepaymentPercent: e.target.value })} placeholder="2" />
                  </div>
                )}
                {legal.prepaymentKind === 'minimum_interest' && (
                  <div>
                    <Label>Minimum Interest Period (months)</Label>
                    <Input value={legal.minimumInterestMonths} onChange={e => setLegal({ ...legal, minimumInterestMonths: e.target.value })} placeholder="6" />
                  </div>
                )}

                <div className="md:col-span-2 flex items-start gap-3">
                  <Checkbox
                    id="opt-notary"
                    checked={legal.notarize}
                    onCheckedChange={(c) => setLegal({ ...legal, notarize: c === true })}
                  />
                  <div>
                    <Label htmlFor="opt-notary" className="cursor-pointer text-sm font-medium">
                      Include Notary Public acknowledgement page
                    </Label>
                    <p className="text-xs text-muted-foreground">Adds jurisdiction, notary signature, and seal blocks for a notarized signing</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground border-t border-border pt-3">
                These clauses are educational templates, not legal advice. Secured loans, prepayment premiums, and
                notarization rules differ by state, province, and country — have a lawyer review the agreement before signing.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <Button onClick={handleOpenReportDialog} className="gap-2">
              <FileDown className="h-4 w-4" />
              Generate PDF Schedule
            </Button>
            {isThirdParty && (
              <Button onClick={handleGenerateThirdPartyDoc} variant="outline" className="gap-2">
                <UserCheck className="h-4 w-4" />
                Generate Borrower Loan Agreement
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Opens in a new window — use your browser's Save as PDF or Print to save.
          </p>
        </CardContent>
      </Card>

      {/* Report Options Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Report Options
            </DialogTitle>
            <DialogDescription>
              Select the sections to include in your generated report.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0 pr-1">
            {/* Loan Purpose & Commitment Summary */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-purpose"
                checked={reportOptions.includePurposeSummary}
                onCheckedChange={(c) => updateReportOption('includePurposeSummary', c === true)}
              />
              <div>
                <Label htmlFor="opt-purpose" className="cursor-pointer text-sm font-medium">
                  Loan Purpose &amp; Commitment Summary
                </Label>
                <p className="text-xs text-muted-foreground">Stated purpose, repayment overview, Family Financing Margin™ at a glance</p>
              </div>
            </div>

            {/* Commitment Schedule */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-commitment"
                checked={reportOptions.includeCommitmentSchedule}
                onCheckedChange={(c) => updateReportOption('includeCommitmentSchedule', c === true)}
              />
              <div>
                <Label htmlFor="opt-commitment" className="cursor-pointer text-sm font-medium">
                  Commitment Schedule
                </Label>
                <p className="text-xs text-muted-foreground">Payment tracking table with checkboxes, dates paid, and notes columns</p>
              </div>
            </div>

            {/* Amortization */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-amortization"
                checked={reportOptions.includeAmortization}
                onCheckedChange={(c) => updateReportOption('includeAmortization', c === true)}
              />
              <div>
                <Label htmlFor="opt-amortization" className="cursor-pointer text-sm font-medium">
                  Intended Loan Amortization Comparison
                </Label>
                <p className="text-xs text-muted-foreground">Side-by-side comparison with a traditional amortized bank loan</p>
              </div>
            </div>

            {/* Estimated Policy Loan Balance column */}
            {reportOptions.includeAmortization && (
              <div className="flex items-start gap-3 ml-7">
                <Checkbox
                  id="opt-policy-col"
                  checked={reportOptions.includeEstimatedPolicyBalance}
                  onCheckedChange={(c) => updateReportOption('includeEstimatedPolicyBalance', c === true)}
                />
                <div>
                  <Label htmlFor="opt-policy-col" className="cursor-pointer text-sm font-medium">
                    Add Estimated Policy Loan Balance column
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Interpolated to each intended-loan payment date. Red while declining, green once the policy is paid and margin rebuilds.
                  </p>
                </div>
              </div>
            )}

            {/* Advisor */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-advisor"
                checked={reportOptions.includeAdvisor}
                onCheckedChange={(c) => updateReportOption('includeAdvisor', c === true)}
              />
              <div>
                <Label htmlFor="opt-advisor" className="cursor-pointer text-sm font-medium">
                  Advisor Details & Signature
                </Label>
                <p className="text-xs text-muted-foreground">Include advisor name in header and signature block</p>
              </div>
            </div>

            {/* Charts */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-charts"
                checked={reportOptions.includeCharts}
                onCheckedChange={(c) => updateReportOption('includeCharts', c === true)}
              />
              <div>
                <Label htmlFor="opt-charts" className="cursor-pointer text-sm font-medium">
                  Balance Trajectory Chart
                </Label>
                <p className="text-xs text-muted-foreground">Visual chart showing projected loan balance over the repayment period</p>
              </div>
            </div>

            {/* Payoff Summary */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-payoff"
                checked={reportOptions.includePayoffSummary}
                onCheckedChange={(c) => updateReportOption('includePayoffSummary', c === true)}
              />
              <div>
                <Label htmlFor="opt-payoff" className="cursor-pointer text-sm font-medium">
                  Estimated Payoff Summary
                </Label>
                <p className="text-xs text-muted-foreground">Payment counts, estimated early payoff, and remaining payments to redirect</p>
              </div>
            </div>

            {/* Financing Margin */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-margin"
                checked={reportOptions.includeFinancingMargin}
                onCheckedChange={(c) => updateReportOption('includeFinancingMargin', c === true)}
              />
              <div>
                <Label htmlFor="opt-margin" className="cursor-pointer text-sm font-medium">
                  Additional Financing Margin Estimate
                </Label>
                <p className="text-xs text-muted-foreground">Estimated margin available and suggested premium redirect amounts</p>
              </div>
            </div>

            {/* Interest Volume */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-volume"
                checked={reportOptions.includeInterestVolume}
                onCheckedChange={(c) => updateReportOption('includeInterestVolume', c === true)}
              />
              <div>
                <Label htmlFor="opt-volume" className="cursor-pointer text-sm font-medium">
                  Interest Volume Estimates
                </Label>
                <p className="text-xs text-muted-foreground">Estimated interest as percentage of total payments — policy vs standard comparison</p>
              </div>
            </div>

            {/* Notes */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-notes"
                checked={reportOptions.includeNotes}
                onCheckedChange={(c) => updateReportOption('includeNotes', c === true)}
              />
              <div className="flex-1">
                <Label htmlFor="opt-notes" className="cursor-pointer text-sm font-medium">
                  Personal Notes
                </Label>
                <p className="text-xs text-muted-foreground">Add context about the loan, repayment source, or your commitment</p>
              </div>
            </div>

            {reportOptions.includeNotes && (
              <div className="ml-7 space-y-1.5">
                <Textarea
                  value={reportOptions.notesText}
                  onChange={(e) => {
                    if (e.target.value.length <= 250) {
                      updateReportOption('notesText', e.target.value);
                    }
                  }}
                  placeholder="E.g., This loan is for a vehicle purchase. Repayments funded from monthly salary. Consolidation includes prior balance from 2024 home renovation. My commitment is to repay on time every month — no exceptions."
                  className="text-sm min-h-[80px]"
                  maxLength={250}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {reportOptions.notesText.length}/250 characters
                </p>
              </div>
            )}

            {/* Lump Sum Scenario Report */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-lumpsum-report"
                checked={reportOptions.includeLumpSumReport}
                onCheckedChange={(c) => updateReportOption('includeLumpSumReport', c === true)}
              />
              <div>
                <Label htmlFor="opt-lumpsum-report" className="cursor-pointer text-sm font-medium">
                  Lump Sum Scenario Report
                </Label>
                <p className="text-xs text-muted-foreground">One-time and recurring lump sums with estimated payoff and margin impact</p>
              </div>
            </div>

            {/* Stewardship */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-stewardship"
                checked={reportOptions.includeStewardship}
                onCheckedChange={(c) => updateReportOption('includeStewardship', c === true)}
              />
              <div>
                <Label htmlFor="opt-stewardship" className="cursor-pointer text-sm font-medium">
                  Family Banking Stewardship™ Assessment
                </Label>
                <p className="text-xs text-muted-foreground">100-point self-assessment with section-by-section breakdown</p>
              </div>
            </div>

            {/* Future Me Letter */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-futureme"
                checked={reportOptions.includeFutureMe}
                onCheckedChange={(c) => updateReportOption('includeFutureMe', c === true)}
              />
              <div>
                <Label htmlFor="opt-futureme" className="cursor-pointer text-sm font-medium">
                  Future Me Letter
                </Label>
                <p className="text-xs text-muted-foreground">Personal letter to your future self about this commitment</p>
              </div>
            </div>

            {/* Family Forum Reminders */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-forum"
                checked={reportOptions.includeFamilyForum}
                onCheckedChange={(c) => updateReportOption('includeFamilyForum', c === true)}
              />
              <div>
                <Label htmlFor="opt-forum" className="cursor-pointer text-sm font-medium">
                  Family Financial Forum™ Reminders
                </Label>
                <p className="text-xs text-muted-foreground">Stewardship prompts for family discussion</p>
              </div>
            </div>

            {/* Why This Commitment Matters */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-why"
                checked={reportOptions.includeWhyMatters}
                onCheckedChange={(c) => updateReportOption('includeWhyMatters', c === true)}
              />
              <div>
                <Label htmlFor="opt-why" className="cursor-pointer text-sm font-medium">
                  &quot;Why This Loan Commitment Matters&quot; Page
                </Label>
                <p className="text-xs text-muted-foreground">Closing philosophy page on discipline, stewardship, and Family Financing Margin™</p>
              </div>
            </div>

            {/* Actual payment history ledger */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="opt-ledger"
                checked={reportOptions.includePaymentLedger}
                disabled={paymentLedger.length === 0}
                onCheckedChange={(c) => updateReportOption('includePaymentLedger', c === true)}
              />
              <div>
                <Label htmlFor="opt-ledger" className="cursor-pointer text-sm font-medium">
                  Actual Payment History &amp; Tracking Ledger
                </Label>
                <p className="text-xs text-muted-foreground">
                  {paymentLedger.length === 0
                    ? 'Available once payments are recorded in the tracking section of a saved loan'
                    : `${paymentLedger.length} recorded payment${paymentLedger.length === 1 ? '' : 's'} with method, cheque #, fees, and notes`}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 shrink-0 pt-2 border-t border-border">
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={handleDownloadPdf} className="gap-2">
              <FileDown className="h-4 w-4" />
              Download PDF
            </Button>
            <Button onClick={handleGeneratePdf} className="gap-2">
              <FileDown className="h-4 w-4" />
              Generate Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
